import { useRef } from 'react';
import { clsx } from 'clsx';
import { EditorEvents, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { NodeClickInfo } from '../ui/CommentInput/CommentInput.types';
import { AgentMention, createMentionSuggestion, type MentionOption } from './agent-mention';

interface UseCommentEditorProps {
  content: Record<string, unknown>;
  editable: boolean;
  onNodeClick?: (info: NodeClickInfo) => void;
  onUpdate: (e: EditorEvents['update']) => void;
  /**
   * When provided, the composer offers an `@` suggestion dropdown over these
   * options. The mention NODE is registered unconditionally (stored mentions
   * must render in read-only editors); only the dropdown is gated.
   */
  mentionOptions?: MentionOption[];
  onFocus?: (e: EditorEvents['focus']) => void;
  onBlur?: (e: EditorEvents['blur']) => void;
  /**
   * Exposes the editor as a named text field. Composers pass their placeholder; read-only
   * comment bodies omit it and stay plain text.
   */
  ariaLabel?: string;
}

// The placeholder is rendered as a positioned React element by `CommentInput`
// (gated on the editor's `isEmpty` state), not via TipTap's Placeholder
// extension. We tried the extension + `attr(data-placeholder)` CSS path; it
// flaked across HMR and Tailwind's JIT. The React overlay is reliable.
export const useCommentEditor = ({
  content,
  editable,
  onNodeClick,
  onUpdate,
  mentionOptions,
  onFocus,
  onBlur,
  ariaLabel,
}: UseCommentEditorProps) => {
  // The editor is created once (useEditor has no deps), so the suggestion must
  // read options through a ref — `mentionOptions` typically resolves from async
  // queries AFTER mount, and a captured snapshot would leave the dropdown empty
  // forever. A composer opts into mentions by passing the prop at all (even an
  // empty array while loading); read-only display editors omit it entirely.
  const mentionOptionsRef = useRef<readonly MentionOption[]>(mentionOptions ?? []);
  mentionOptionsRef.current = mentionOptions ?? [];
  const mentionsEnabled = mentionOptions !== undefined;

  const editor = useEditor({
    content,
    editable,
    onFocus,
    onBlur,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
        blockquote: false,
        bold: false,
        italic: false,
        strike: false,
        code: false,
      }),
      mentionsEnabled
        ? AgentMention.configure({
            suggestion: createMentionSuggestion(() => mentionOptionsRef.current),
          })
        : AgentMention,
    ],
    onUpdate,
    editorProps: {
      attributes: {
        class: clsx(
          'text-sm leading-relaxed outline-none',
          editable && 'min-h-4',
          '[&_p]:m-0',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
          '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
        ),
        // Passing `attributes` at all replaces the `role="textbox"` TipTap adds by default, so
        // a composer restates it alongside its name. Once the role is textbox, assistive
        // technology reads editability from aria-readonly rather than contenteditable.
        /* eslint-disable i18next/no-literal-string -- ARIA attributes, not copy */
        ...(ariaLabel
          ? {
              role: 'textbox',
              'aria-multiline': 'true',
              'aria-label': ariaLabel,
              'aria-readonly': String(!editable),
            }
          : {}),
        /* eslint-enable i18next/no-literal-string */
      },
      handleClickOn(_view, _pos, node) {
        if (!onNodeClick || !node.isInline) return false;
        onNodeClick({ nodeType: node.type.name, attrs: node.attrs as Record<string, unknown> });
        return true;
      },
    },
  });

  return editor;
};
